// /app/frontend/src/pages/seo/HoustonBlackDatingPage.jsx
// City landing page — Houston Black dating community.
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  buildBreadcrumbSchema,
  GradientHero,
  FeatureCard,
  RelatedGuides,
  BottomCTA,
} from './SEOUtils';

export default function HoustonBlackDatingPage() {
  const path = '/houston-black-dating';
  const schemas = [
    organizationSchema,
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Guides', url: '/guides' },
      { name: 'Houston Black Dating', url: path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Houston Black Dating Community"
        description="UnityLove connects Black singles, couples, and families across Houston seeking meaningful relationships, intentional dating, and a real community."
        canonical={path}
        schemas={schemas}
      />

      <GradientHero
        eyebrow="Houston, Texas"
        headline="Building Connections In Houston"
        sub="A city of neighborhoods, of communities, of one of the most diverse Black populations in America. Houston dating deserves a platform that matches its depth."
        body="From the Third Ward to Sugar Land, from Acres Homes to Pearland — UnityLove connects intentional singles, couples, and families across H-Town."
        primaryCta={{ label: 'Create Your Profile Free', to: '/auth?mode=signup' }}
        secondaryCta={{ label: 'Browse All Guides', to: '/guides' }}
      />

      <main className="max-w-4xl mx-auto px-4 py-14">
        {/* Why Houston */}
        <section className="mb-12">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-4">
            Why Houston
          </h2>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              Houston is one of the largest and most ethnically diverse Black cities in America — a major hub for African Americans, Africans from across the continent, Afro-Latinos, and Afro-Caribbean families. It is the city that produced Beyoncé and Megan Thee Stallion, Travis Scott and Lizzo, generations of athletes and activists, and a music scene that shaped American culture from screwed-and-chopped onward.
            </p>
            <p>
              The dating scene reflects all of it. Houston dating is shaped by Texas Southern University and Prairie View, by mega-churches and hole-in-the-wall Sunday breakfast spots, by the kind of Southern Black hospitality that makes Houston feel like everywhere at once. UnityLove builds for the full picture.
            </p>
          </div>
        </section>

        {/* Who's here */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-6 text-center">
            Who You&rsquo;ll Find In Houston
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <FeatureCard
              icon="🎓"
              accent="amber"
              title="HBCU Alumni & Students"
              body="Texas Southern University, Prairie View A&M — generations of Houston-rooted alumni and networks that keep the city culturally connected."
            />
            <FeatureCard
              icon="🌍"
              accent="emerald"
              title="A Global Black Diaspora"
              body="One of the most diverse Black populations in America — African Americans, Nigerians, Ghanaians, Ethiopians, Jamaicans, Afro-Latinos. The whole diaspora dates here."
            />
            <FeatureCard
              icon="⛽"
              accent="rose"
              title="Black Professionals & Entrepreneurs"
              body="A city built on energy, medical, and tech — alongside one of the strongest Black entrepreneur ecosystems in the South. Ambitious, busy, intentional daters."
            />
            <FeatureCard
              icon="⛪"
              accent="violet"
              title="Faith-Centered Community"
              body="Houston has some of the most active and influential Black church communities in America, plus growing Muslim, African traditional, and secular spiritual circles."
            />
          </div>
        </section>

        {/* Around the city */}
        <section className="mb-14 bg-slate-50 rounded-3xl p-6 sm:p-8">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
            Around H-Town
          </h2>
          <p className="text-slate-700 leading-relaxed mb-4">
            Houston dating is shaped by which part of the city you call home:
          </p>
          <ul className="grid gap-3 sm:grid-cols-2 text-slate-700">
            <li><strong className="text-slate-900">Third Ward</strong> — Cultural heartland, TSU energy, soulful spots</li>
            <li><strong className="text-slate-900">Acres Homes / Fifth Ward</strong> — Family neighborhoods, deep roots, Sunday culture</li>
            <li><strong className="text-slate-900">Midtown / Montrose</strong> — Date-night dining, walkable scene, younger crowd</li>
            <li><strong className="text-slate-900">Sugar Land / Pearland / Missouri City</strong> — Established Black families, suburban professionals</li>
            <li><strong className="text-slate-900">Sunnyside / South Park</strong> — Historic Black communities, family-rooted</li>
            <li><strong className="text-slate-900">Greater Greenspoint / North Houston</strong> — Diaspora hubs, especially Nigerian and Ethiopian communities</li>
          </ul>
        </section>

        {/* What's different */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-3">
            What Makes Houston Dating Different
          </h2>
          <div className="space-y-3 text-slate-700 leading-relaxed">
            <p>
              Houston is sprawling — the city is bigger than most people from outside Texas realize, and the dating pool spreads across an enormous metro. That can make traditional dating apps feel chaotic; you match with someone forty-five minutes away, and the logistics kill the spark before the first date.
            </p>
            <p>
              UnityLove approaches it differently. Verified, intentional members. Profile prompts that signal long-term intent. Video lounges to skip the &ldquo;is the drive worth it&rdquo; phase. The diaspora diversity becomes a feature instead of a noise problem — people who actually want a Nigerian partner, a Caribbean partner, or a Southern Black partner can find each other clearly.
            </p>
          </div>
        </section>

        {/* Internal links */}
        <section className="mb-4">
          <h2 className="font-playfair text-2xl font-bold text-slate-900 mb-4">
            Continue Reading
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <a href="/black-love-dating-app" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Black Love Dating App</p>
              <p className="text-sm text-slate-600 mt-1">Centering culture and connection.</p>
            </a>
            <a href="/african-diaspora-dating" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">African Diaspora Dating</p>
              <p className="text-sm text-slate-600 mt-1">Across the global Black community.</p>
            </a>
            <a href="/atlanta-black-dating" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Atlanta Black Dating</p>
              <p className="text-sm text-slate-600 mt-1">Connections across the ATL.</p>
            </a>
            <a href="/dating-app-for-intentional-relationships" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Dating App For Intentional Relationships</p>
              <p className="text-sm text-slate-600 mt-1">For people looking for more than casual.</p>
            </a>
          </div>
        </section>
      </main>

      <RelatedGuides currentPath={path} />
      <BottomCTA />
    </div>
  );
}
